import { Filter, Grid3x3, List } from 'lucide-react'

interface ResultsHeaderProps {
  loading: boolean
  roomCount: number
  checkIn?: string
  checkOut?: string
  guests?: string
  pagination?: {
    from: number
    to: number
    total: number
  }
  viewMode: 'grid' | 'list'
  sortBy: string
  onViewModeChange: (mode: 'grid' | 'list') => void
  onSortChange: (sortBy: string) => void
  onFilterClick: () => void
}

const ResultsHeader = ({
  loading,
  roomCount,
  checkIn,
  checkOut,
  guests,
  pagination,
  viewMode,
  sortBy,
  onViewModeChange,
  onSortChange,
  onFilterClick
}: ResultsHeaderProps) => {
  return (
    <div className="mb-4 sm:mb-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex-1">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">
            {loading ? 'Loading...' : `${roomCount} Propert${roomCount !== 1 ? 'ies' : 'y'} Found`}
          </h2>
          <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
            {checkIn && checkOut && (
              <>
                <span>Check-in: {checkIn}</span>
                <span>•</span>
                <span>Check-out: {checkOut}</span>
              </>
            )}
            {guests && guests !== 'all' && (
              <>
                {checkIn && <span>•</span>}
                <span>{guests} Guests</span>
              </>
            )}
          </div>
          {pagination && (
            <p className="text-xs sm:text-sm text-gray-500 mt-1">
              Showing {pagination.from}-{pagination.to} of {pagination.total} results
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* View Mode Toggle - Desktop Only */}
          <div className="hidden md:flex items-center gap-1 bg-white rounded-lg p-1 border border-gray-200">
            <button
              onClick={() => onViewModeChange('grid')}
              className={`p-2 rounded transition-colors ${
                viewMode === 'grid'
                  ? 'bg-primary-500 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Grid3x3 className="h-4 w-4" />
            </button>
            <button
              onClick={() => onViewModeChange('list')}
              className={`p-2 rounded transition-colors ${
                viewMode === 'list'
                  ? 'bg-primary-500 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <List className="h-4 w-4" />
            </button>
          </div>
          {/* Sort Dropdown */}
          <select
            value={sortBy}
            onChange={(e) => onSortChange(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 text-sm bg-white"
          >
            <option value="latest">Latest</option>
            <option value="price-low">Price: Low to High</option>
            <option value="price-high">Price: High to Low</option>
            <option value="rating">Highest Rated</option>
            <option value="popular">Most Popular</option>
          </select>
          {/* Mobile Filter Button */}
          <button 
            onClick={onFilterClick}
            className="md:hidden flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors shadow-md"
          >
            <Filter className="h-4 w-4" />
            <span className="text-sm font-medium">Filters</span>
          </button>
        </div>
      </div>
    </div>
  )
}

export default ResultsHeader

